import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Users, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/services/users.api';

/**
 * Bouton dropdown + liste à cases à cocher pour sélectionner plusieurs
 * collègues. Utilisé par la page Calendrier (vue « Collègues ») et par la
 * page Activité (filtre collaborateurs).
 *
 * Le dropdown se ferme au clic extérieur et adapte son label déclencheur
 * selon le nombre d'éléments sélectionnés.
 */
export interface ColleagueMultiSelectProps {
  members: TeamMember[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Label affiché dans le bouton quand aucune sélection. Défaut: `Voir un collègue`. */
  placeholder?: string;
  /** Titre du bandeau d'en-tête du popover. Défaut: `Collègues`. */
  headerLabel?: string;
  /** Texte à afficher au pluriel, ex. `3 collègues` / `3 collaborateurs`. */
  pluralLabel?: string;
}

export function ColleagueMultiSelect({
  members,
  selected,
  onChange,
  placeholder = 'Voir un collègue',
  headerLabel = 'Collègues',
  pluralLabel = 'collègues',
}: ColleagueMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  }

  const selectedMembers = selected
    .map((id) => members.find((m) => m.id === id))
    .filter((m): m is TeamMember => !!m);

  const triggerLabel =
    selectedMembers.length === 0
      ? placeholder
      : selectedMembers.length === 1
        ? `${selectedMembers[0]!.firstName} ${selectedMembers[0]!.lastName}`
        : `${selectedMembers.length} ${pluralLabel}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
          selectedMembers.length > 0
            ? 'border-accent bg-accent/5 text-accent'
            : 'border-border-subtle bg-white text-text-muted hover:text-brand',
        )}
      >
        <Users className="h-3.5 w-3.5" />
        {triggerLabel}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 top-full z-20 mt-1 max-h-72 w-72 overflow-y-auto rounded-md border border-border-subtle bg-white shadow-popover"
        >
          <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              {headerLabel}
            </span>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="inline-flex items-center gap-1 text-[11px] text-text-muted hover:text-accent"
              >
                <X className="h-3 w-3" />
                Tout désélectionner
              </button>
            )}
          </div>
          {members.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-text-muted">
              Aucun {pluralLabel.replace(/s$/, '')}.
            </div>
          ) : (
            <ul>
              {members.map((m) => {
                const checked = selected.includes(m.id);
                return (
                  <li key={m.id}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50',
                        checked && 'bg-accent/5',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(m.id)}
                        className="accent-accent"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-brand">
                          {m.firstName} {m.lastName}
                        </div>
                        <div className="truncate text-[11px] text-text-muted">{m.email}</div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
